import { GridPaginationModel } from '@mui/x-data-grid-premium'
import { partialMatchKey, useQuery } from '@tanstack/react-query'
import chunk from 'lodash/chunk'
import range from 'lodash/range'
import uniqBy from 'lodash/uniqBy'
import { getPollingGCTime } from '@omnea-app/fe-api-client/queryConfig'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { transformMuiFilterModelToOmnea, transformMuiSortModelToOmnea } from '@omnea-app/fe-tables/utils/transformMuiGridModels'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { TasksTopLevelFilterKey } from '@omnea-app/tasks/tasksFrontendClient'
import { TasksHttpClient } from '@omnea-app/tasks/tasksHttpClient'
import { taskQueryKeys } from './queryKeys'
import { isPlainServerTaskQuery, ServerTaskQuery } from './utils/toServerTaskQuery'

const FIVE_MINUTES = 5 * 60_000

const tasksPerBatch = 100

// Every page re-runs the full task query on the read replica, so a user with thousands of tasks must not
// fire all their pages at once.
const maxConcurrentBatches = 3

const fetchTasksBatch = async (
  apiClient: TasksHttpClient,
  paginationModel: GridPaginationModel,
  topLevelFilterKey: TasksTopLevelFilterKey | null,
  // The endpoint counts unless told not to.
  { includeTotalCount, signal }: { includeTotalCount?: false; signal?: AbortSignal } = {}
) => {
  const { data, error } = await apiClient.POST('/', {
    body: { paginationModel, topLevelFilter: topLevelFilterKey ?? undefined, includeTotalCount },
    signal,
  })
  throwIfApiError(error)
  if (!data) {
    throw new Error('No data')
  }
  return data
}

interface Props {
  topLevelFilterKey: TasksTopLevelFilterKey | null
  enabled?: boolean
}

export const useTasks = ({ topLevelFilterKey, enabled = true }: Props) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: taskQueryKeys.list(topLevelFilterKey),
    enabled,
    refetchInterval: FIVE_MINUTES,
    gcTime: getPollingGCTime(FIVE_MINUTES),
    queryFn: async ({ signal }) => {
      const token = await getToken()
      const tasksApiClient = new TasksHttpClient({ authorization: token })

      const firstBatch = await fetchTasksBatch(tasksApiClient, { page: 0, pageSize: tasksPerBatch }, topLevelFilterKey, {
        signal,
      })
      const totalPages = Math.ceil((firstBatch.totalCount ?? 0) / tasksPerBatch)
      if (totalPages < 2) {
        return firstBatch.tasks
      }

      // A failed page throws (fetchTasksBatch), failing the whole list rather than returning it with a gap. Once
      // React Query cancels the fetch (e.g. the tab changed), the aborted signal fails the next wave straight away.
      const allTasks = [...firstBatch.tasks]
      for (const pages of chunk(range(1, totalPages), maxConcurrentBatches)) {
        const batches = await Promise.all(
          pages.map((page) =>
            fetchTasksBatch(tasksApiClient, { page, pageSize: tasksPerBatch }, topLevelFilterKey, {
              includeTotalCount: false,
              signal,
            })
          )
        )
        allTasks.push(...batches.flatMap((data) => data.tasks))
      }

      // remove duplicates between batches just in case
      return uniqBy(allTasks, 'id')
    },
  })
}

type TasksPageProps = {
  topLevelFilterKey: TasksTopLevelFilterKey | null
  paginationModel: GridPaginationModel
  query: ServerTaskQuery
  enabled: boolean
}

// One page of the grid's view, filtered, searched and sorted by the server: an unfiltered view from the existing
// endpoint, anything else from /paginated.
export const useTasksPage = ({ topLevelFilterKey, paginationModel: { page, pageSize }, query, enabled }: TasksPageProps) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: taskQueryKeys.page(topLevelFilterKey, page, pageSize, query),
    enabled,
    refetchInterval: FIVE_MINUTES,
    gcTime: getPollingGCTime(FIVE_MINUTES),
    // Keep the previous page on screen while the next one loads, but never another tab's rows.
    placeholderData: (previousData, previousQuery) =>
      previousQuery && partialMatchKey(previousQuery.queryKey, taskQueryKeys.list(topLevelFilterKey)) ? previousData : undefined,
    queryFn: async () => {
      const token = await getToken()
      const tasksApiClient = new TasksHttpClient({ authorization: token })
      if (isPlainServerTaskQuery(query)) {
        const { tasks, totalCount } = await fetchTasksBatch(tasksApiClient, { page, pageSize }, topLevelFilterKey)
        // The count is only omitted when a caller opts out, which this request doesn't.
        return { tasks, totalCount: totalCount ?? 0 }
      }
      const { data, error } = await tasksApiClient.POST('/paginated', {
        body: {
          paginationModel: { page, pageSize },
          filterModel: transformMuiFilterModelToOmnea({ filterModel: query.filterModel }),
          sortModel: transformMuiSortModelToOmnea({ sortModel: query.sortModel }),
          topLevelFilter: topLevelFilterKey ?? undefined,
        },
      })
      throwIfApiError(error)
      if (!data) {
        throw new Error('No data')
      }
      return data
    },
  })
}

const MY_TASK_COUNT_FILTER_KEY = 'pending'

export const useGetMyTaskCount = () => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: taskQueryKeys.listCount(MY_TASK_COUNT_FILTER_KEY),
    refetchInterval: FIVE_MINUTES,
    gcTime: getPollingGCTime(FIVE_MINUTES),
    queryFn: async () => {
      const token = await getToken()
      const tasksApiClient = new TasksHttpClient({ authorization: token })

      const { data } = await tasksApiClient.POST('/', {
        body: { paginationModel: { page: 0, pageSize: 0 }, topLevelFilter: MY_TASK_COUNT_FILTER_KEY },
      })

      return data?.totalCount ?? 0
    },
  })
}
